import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button, Flex, Table, Spin, Tooltip, message } from 'antd'
import type { TableProps } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { SearchInput } from '@/components/SearchInput'
import Title from 'antd/es/typography/Title'
import { type Role } from '@/data'
import { getRoles } from '@/api/roles/getRoles'
import { deleteRole } from '@/api/roles/deleteRole'
import { PermissionTag } from '@/components/PermissionTag'
import { sortItemsByLabel } from '@/utils/selectSorting'
import { AuthContext } from '@/contexts/AuthContext'
import { hasPermission } from '@/auth/access'
import { getErrorMessage } from '@/utils/getErrorMessage'

export const AdminRolesPage: React.FC = () => {
  const navigate = useNavigate()
  const auth = useContext(AuthContext)!
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  })
  const [search, setSearch] = useState('')

  const hasFetched = useRef(false)
  const canReadRoles =
    hasPermission(auth.user, 'lop:ui:read:create:roles') ||
    hasPermission(auth.user, 'lop:ui:path:read:/admin/roles')
  const canCreateRoles = hasPermission(auth.user, 'lop:ui:action:create:roles')
  const canUpdateRoles = hasPermission(auth.user, 'lop:ui:action:update:roles')
  const canDeleteRoles = hasPermission(auth.user, 'lop:ui:action:delete:roles')

  const fetchRoles = useCallback(
    async (page = 1, query = search) => {
      try {
        setLoading(true)
        setError(null)
        const { data, meta } = await getRoles({
          page,
          ...(query ? { filters: [{ key: 'name', value: query }] } : {}),
        })
        setRoles(data)
        setPagination({
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
        })
      } catch (err) {
        setError('Failed to load roles')
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [search],
  )

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value)
      fetchRoles(1, value)
    },
    [fetchRoles],
  )

  useEffect(() => {
    if (!canReadRoles) {
      setLoading(false)
      return
    }

    if (hasFetched.current) return
    hasFetched.current = true
    fetchRoles()
  }, [canReadRoles, fetchRoles])

  const handleDelete = async (id: number) => {
    try {
      await deleteRole(String(id))
      message.success('Role deleted')
      fetchRoles(pagination.current)
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to delete role'))
      console.error(err)
    }
  }

  const columns: TableProps<Role>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) =>
        canUpdateRoles ? (
          <Link to={`/admin/roles/${record.id}/edit`}>{text}</Link>
        ) : (
          text
        ),
    },
    {
      title: 'Guard',
      dataIndex: 'guard_name',
      key: 'guard_name',
      sorter: (a, b) => a.guard_name.localeCompare(b.guard_name),
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (_, record) => {
        if (record.permissions == null || record.permissions.length === 0) {
          return '-'
        }

        const sortedPermissions = sortItemsByLabel(
          record.permissions,
          (permission) => permission.name,
        )

        return (
          <Flex gap={4} wrap>
            {sortedPermissions.map((permission) => (
              <PermissionTag key={permission.id} name={permission.name} />
            ))}
          </Flex>
        )
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          {canUpdateRoles ? (
            <Tooltip title="Edit">
              <Link to={`/admin/roles/${record.id}/edit`}>
                <EditOutlined style={{ fontSize: 16, marginRight: 12 }} />
              </Link>
            </Tooltip>
          ) : null}
          {canDeleteRoles ? (
            <Tooltip title="Delete">
              <DeleteOutlined
                style={{ fontSize: 16, color: '#ff4d4f' }}
                onClick={() => handleDelete(record.id)}
              />
            </Tooltip>
          ) : null}
        </>
      ),
    },
  ]

  if (error) {
    return (
      <div>
        <Title level={4}>Roles</Title>
        <p style={{ color: '#ff4d4f' }}>{error}</p>
        <Button onClick={() => fetchRoles()}>Retry</Button>
      </div>
    )
  }

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Roles
        </Title>
        <Flex gap="small" align="center">
          <SearchInput onSearch={handleSearch} />
          {canCreateRoles ? (
            <Button type="primary" onClick={() => navigate('/admin/roles/new')}>
              New
            </Button>
          ) : null}
        </Flex>
      </Flex>
      <Spin spinning={loading}>
        <Table<Role>
          rowKey="id"
          columns={columns}
          dataSource={roles}
          pagination={pagination}
          onChange={(p) => fetchRoles(p.current, search)}
        />
      </Spin>
    </div>
  )
}
